import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, AlertCircle } from 'lucide-react'
import { Report } from '../types/reports'

interface VideoModeration {
  id: string
  user_id: string
  upload_id: string
  proposed_video_url: string
  status: 'pending' | 'approved' | 'rejected'
  rejection_reason?: string
  reviewed_at?: string
  created_at: string
  updated_at?: string
}

const approvalSchema = z.object({})

const rejectionSchema = z.object({
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
})

const blockSchema = z.object({
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
})

const ignoreSchema = z.object({})

interface ActionModalProps {
  video?: VideoModeration
  report?: Report
  actionType: 'approve' | 'reject' | 'block' | 'ignore'
  onClose: () => void
  onApprove?: (data: { videoId: string }) => void
  onReject?: (data: { videoId: string; reason: string }) => void
  onBlock?: (data: { reportId: string; reason: string }) => void
  onIgnore?: (data: { reportId: string }) => void
  isLoading?: boolean
}

export function ActionModal({
  video,
  report,
  actionType,
  onClose,
  onApprove,
  onReject,
  onBlock,
  onIgnore,
  isLoading = false,
}: ActionModalProps) {
  const [error, setError] = useState('')

  const getSchema = () => {
    switch (actionType) {
      case 'approve':
        return approvalSchema
      case 'reject':
        return rejectionSchema
      case 'block':
        return blockSchema
      case 'ignore':
        return ignoreSchema
      default:
        return z.object({})
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(getSchema()),
  })

  const onSubmit = async (data: any) => {
    setError('')
    
    try {
      if (actionType === 'approve' && video && onApprove) {
        await onApprove({ videoId: video.id })
      } else if (actionType === 'reject' && video && onReject) {
        await onReject({
          videoId: video.id,
          reason: data.reason
        })
      } else if (actionType === 'block' && report && onBlock) {
        await onBlock({
          reportId: report.id,
          reason: data.reason
        })
      } else if (actionType === 'ignore' && report && onIgnore) {
        await onIgnore({
          reportId: report.id
        })
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Action failed')
    }
  }

  const getTitle = () => {
    switch (actionType) {
      case 'approve':
        return 'Approve Video'
      case 'reject':
        return 'Reject Video'
      case 'block':
        return 'Delete User Account'
      case 'ignore':
        return 'Ignore Report'
      default:
        return 'Action Required'
    }
  }

  const getDescription = () => {
    switch (actionType) {
      case 'approve':
        return 'This video will be approved and made visible to users.'
      case 'reject':
        return 'This video will be rejected and the user will be notified with the reason you provide.'
      case 'block':
        return 'This user account will be permanently deleted and the user will be notified. This action cannot be undone!'
      case 'ignore':
        return 'This report will be marked as ignored with no action taken.'
      default:
        return ''
    }
  }

  const getButtonText = () => {
    switch (actionType) {
      case 'approve':
        return 'Approve Video'
      case 'reject':
        return 'Reject Video'
      case 'block':
        return 'Delete Account'
      case 'ignore':
        return 'Ignore Report'
      default:
        return 'Confirm'
    }
  }

  const getButtonClass = () => {
    switch (actionType) {
      case 'approve':
        return 'btn-success'
      case 'reject':
      case 'block':
        return 'btn-danger'
      case 'ignore':
        return 'btn-secondary'
      default:
        return 'btn-primary'
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

        <div className={`relative ${actionType === 'block' ? 'bg-red-100' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full`}>
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{getTitle()}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
            <p className="text-sm text-gray-600">{getDescription()}</p>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            {actionType === 'reject' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection Reason *
                </label>
                <textarea
                  {...register('reason')}
                  rows={3}
                  className="input"
                  placeholder="Provide a clear reason why this video is being rejected..."
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason.message as string}</p>
                )}
              </div>
            )}

            {actionType === 'block' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deletion Reason *
                </label>
                <textarea
                  {...register('reason')}
                  rows={3}
                  className="input"
                  placeholder="Provide a clear reason why this account is being deleted..."
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason.message as string}</p>
                )}
              </div>
            )}

            {actionType === 'block' ?
              <div className="flex justify-between space-x-3 pt-4">
                <button
                  type="submit"
                  className={`btn ${getButtonClass()} px-4 py-2`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    getButtonText()
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary px-4 py-2"
                  disabled={isLoading}
                  style={{ backgroundColor: 'white', color: 'black' }}
                >
                  Cancel
                </button>
              </div>
              :
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary px-4 py-2"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn ${getButtonClass()} px-4 py-2`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    getButtonText()
                  )}
                </button>
              </div>
            }
          </form>
        </div>
      </div>
    </div>
  )
}
